import { useEffect, useState } from "react";

/**
 * "El sello que espera" — the library-nav signal store (T297).
 *
 * A single source of truth in `localStorage`, shared across every nav mount
 * (mobile tab bar + desktop appbar) and across tabs:
 *
 *   KEY = 'tfc.biblioteca.pendingCeremony'  →  JSON: [{ id, title }, …]
 *
 * · The PLAYER, on a genuine technique unlock (session completion), calls
 *   `pushPendingTechnique({ id, title })`.
 * · The nav seal is shown while `length > 0` (`useLibrarySignal`).
 * · The BIBLIOTECA, when the Acquisition Ceremony for a technique plays
 *   (Acto V), calls `witnessTechnique(id)` — or `clearPendingTechniques()`.
 *
 * Client-only state (never a source of truth — the server's `unlockedAt` is).
 * It survives reloads so the seal persists until the member sees the ceremony.
 */
const KEY = "tfc.biblioteca.pendingCeremony";
const EVT = "tfc:library-signal";

export type PendingTechnique = { id: string; title: string };

function read(): PendingTechnique[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? (parsed as PendingTechnique[]) : [];
  } catch {
    return [];
  }
}

function write(list: PendingTechnique[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(list));
  } catch {
    /* storage blocked (private mode) — the signal degrades to in-memory only */
  }
  // The browser's `storage` event does NOT fire in the tab that wrote it, so we
  // dispatch our own event to sync mounts in the SAME document.
  window.dispatchEvent(new CustomEvent(EVT, { detail: list }));
}

/** Player: add a freshly-unlocked technique (idempotent on id). */
export function pushPendingTechnique(tech: PendingTechnique): void {
  if (!tech?.id) return;
  const list = read();
  if (list.some((t) => t.id === tech.id)) return;
  write(list.concat([{ id: tech.id, title: tech.title || "" }]));
}

/** Biblioteca: the member saw this technique's ceremony → drop it. */
export function witnessTechnique(id: string): void {
  if (!id) return;
  // Read once: comparing against a second read() opens a TOCTOU window where
  // another tab's write between the two reads skews the length check.
  const current = read();
  const next = current.filter((t) => t.id !== id);
  if (next.length !== current.length) write(next);
}

/** Drop every pending technique (global clear). */
export function clearPendingTechniques(): void {
  if (read().length > 0) write([]);
}

/**
 * Seed the signal from the server's real unlock state (T297). The player push
 * only fires at the instant of a live completion; this catches techniques that
 * unlocked earlier (already-completed sessions, the async gamification queue,
 * another device) so the seal reflects "you have an unlocked technique whose
 * ceremony you haven't seen yet" — not just this session's completions.
 *
 * Idempotent and respectful of "seen": skips arming when a
 * `tfc:cerem-seen:<id>:<unlockedAt>` marker already exists (the same key the
 * acquisition ceremony writes), and `pushPendingTechnique` no-ops if it's
 * already pending.
 *
 * T355 — `latest` is seen-aware server truth (`null` = nothing unseen, per the
 * member's cross-device `techniques_seen_at` watermark). On `null`, any local
 * pending entries are stale echoes of unlocks already witnessed on another
 * device: clear them instead of letting this device's seal stay lit forever.
 */
export function reconcileFromServer(latest: { id: string; unlockedAt: string } | null): void {
  if (typeof window === "undefined") return;
  if (!latest?.id) {
    clearPendingTechniques();
    return;
  }
  try {
    if (window.localStorage.getItem(`tfc:cerem-seen:${latest.id}:${latest.unlockedAt}`)) return;
  } catch {
    /* storage blocked — fall through and arm (worst case: seal shows once) */
  }
  pushPendingTechnique({ id: latest.id, title: "" });
}

/**
 * Subscribe a nav mount to the signal. Re-renders on any change, whether it
 * came from this tab (`tfc:library-signal`) or another (`storage`).
 */
export function useLibrarySignal(): PendingTechnique[] {
  // Hydration-safe: seed with the SSR value ([], since the server has no
  // localStorage) so the FIRST client render matches the server HTML. Seeding
  // from read() here would diverge whenever a technique is pending → React
  // hydration mismatch (error 418) → whole-tree client re-render that replays
  // the page's entrance animations (the authenticated "double load" flash).
  // The mount effect below syncs to the real localStorage value one tick later.
  const [list, setList] = useState<PendingTechnique[]>([]);
  useEffect(() => {
    const sync = () => setList(read());
    const onStorage = (e: StorageEvent) => {
      if (e.key === KEY || e.key === null) sync();
    };
    // Read on mount (post-hydration): the store's real value + any change that
    // landed during SSR→hydration is applied here, safely after the first paint.
    sync();
    window.addEventListener(EVT, sync);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(EVT, sync);
      window.removeEventListener("storage", onStorage);
    };
  }, []);
  return list;
}
